import React, { useState } from 'react';
import moment from 'moment';
import copyToClipboard from 'copy-to-clipboard';

import { DATA_TABLE_SOURCE_TENANT_BULK_IMPORT_SYNCS_FROM_GMAIL } from 'common/dataTableConstants';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import { SR_API_ACTION_UTILITY_PARTNER_ASSIGN_TO_GMAIL_SENDER } from 'api/utilityActionTypes';

import { SR_TABLE_THEME_HISTORICAL } from 'client/components/SrTable';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrTooltip, {
  SR_TOOLTIP_POSITION_LEFT,
  SR_TOOLTIP_POSITION_CENTER,
} from 'client/components/SrTooltip';
import UPPartnerSearchForm from 'client/components/UPPartnerSearchForm/UPPartnerSearchForm';

import {
  decodeGmailSenderLabel,
  truncateSenderId,
  groupFilesBySenderId,
} from 'client/helpers/gmailSenderId';

// Slight grey wash shown under whichever single row the cursor is over.
const ROW_HOVER_HIGHLIGHT_COLOR = '#F2F4F5';
// Light red wash shown on EVERY row of a duplicate group when any of them is hovered, so the
// rows that would be assigned together stand out as needing attention.
const DUPLICATE_GROUP_HIGHLIGHT_COLOR = '#FDE7E7';
// Left padding added to a child (duplicate) row's first cell so it reads as nested under its parent.
const CHILD_ROW_INDENT = '28px';

function getDataTableSettingsFromProps() {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_TENANT_BULK_IMPORT_SYNCS_FROM_GMAIL,
    rootViewId: DATA_TABLE_SOURCE_TENANT_BULK_IMPORT_SYNCS_FROM_GMAIL,
    initialViewSettings: {
      filter: {
        utilityPartnerId: null,
      },
      loadAllData: true,
    },
    itemsPropName: 'filesToMap',
  };
  return dataTableSettings;
}

const UPAdminPendingGmailAttachmentMappingsDataTable = ({ listingId }) => {
  const [copied, setCopied] = useState(null);
  const [assignPartnerModalArgs, setAssignPartnerModalArgs] = useState(null);
  const [assignPartnerError, setAssignPartnerError] = useState(null);
  // The row the cursor is currently over: { rowId, senderId }. Drives the slight-grey single-row
  // hover and the red highlight applied to a whole duplicate group.
  const [hoveredRow, setHoveredRow] = useState(null);
  // Set of gmailSenderIds whose duplicate children are currently expanded.
  const [expandedSenderIds, setExpandedSenderIds] = useState(() => new Set());

  const toggleExpanded = (senderId) => {
    setExpandedSenderIds((prev) => {
      const next = new Set(prev);
      if (next.has(senderId)) {
        next.delete(senderId);
      } else {
        next.add(senderId);
      }
      return next;
    });
  };

  const dispatch = useDispatch();

  const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY HH:mm A');
  };

  // Instructional legend shown above the table: what the tool is for, how the grouping works,
  // and how to read the two status icons.
  const renderLegend = () => (
    <SrBox
      marginBottom="16px"
      padding="12px 16px"
      border="1px solid #E1E5E8"
      borderRadius="8px"
      backgroundColor="#F7F9FA"
    >
      <SrText font="greycliff" weight="700" fontSize="14px" color="black" display="block">
        How to use this tool
      </SrText>
      <SrText
        font="greycliff"
        fontSize="13px"
        color="black"
        display="block"
        marginTop="4px"
        lineHeight="1.45"
      >
        Each row is a property-management file pulled from Gmail that still needs a partner. Click{' '}
        <SrText font="greycliff" weight="700" fontSize="13px" color="black">
          Assign to Partner
        </SrText>{' '}
        to map a file. Files are grouped by their exact{' '}
        <SrText font="greycliff" weight="700" fontSize="13px" color="black">
          Gmail sender ID
        </SrText>{' '}
        — the same key used when assigning — so you can tell when several files come from the very
        same sender (not just a sender with the same display name). Files that share an exact Gmail
        sender ID are collapsed into one row; click it to expand the duplicates.
      </SrText>
      <SrBox display="flex" alignItems="center" gap="8px" marginTop="10px">
        <SrIcon className="warning-icon" />
        <SrText font="greycliff" fontSize="13px" color="black">
          <SrText font="greycliff" weight="700" fontSize="13px" color="black">
            Duplicate group
          </SrText>{' '}
          — “N files” share this exact Gmail sender ID. Click the row to expand them (hovering
          highlights the whole group in red). Only the top row is mappable; assigning it maps them
          all.
        </SrText>
      </SrBox>
      <SrBox display="flex" alignItems="center" gap="8px" marginTop="6px">
        <SrIcon className="green-checkmark-small-thick" />
        <SrText font="greycliff" fontSize="13px" color="black">
          <SrText font="greycliff" weight="700" fontSize="13px" color="black">
            Safe to map 1:1
          </SrText>{' '}
          — no other file shares this Gmail sender ID.
        </SrText>
      </SrBox>
    </SrBox>
  );

  // Renders the "Email Sender Id" cell. We show a short base64 token (e.g. `W0ZkZG9u…UHJvZml0`) and,
  // on hover, the decoded sender name, the file source slug, and the full raw gmailSenderId.
  //   role 'single' — group of one file: green "Safe to map 1:1" check.
  //   role 'parent' — representative of a duplicate group: chevron + ⚠️ + "N files" count.
  //   role 'child'  — a duplicate revealed on expand: indented, no flag, not mappable.
  const renderSenderIdCell = ({ fileToMap, count, role, isExpanded }) => {
    const name = decodeGmailSenderLabel(fileToMap.gmailSenderId);
    const token = truncateSenderId(fileToMap.gmailSenderId, 8, 8);
    const sourceSlug = getEmailSenderFromUrl(fileToMap.url);

    return (
      <SrBox display="flex" alignItems="center" gap="6px">
        {role === 'child' && (
          <SrText font="greycliff" color="grey400" fontSize="14px">
            ↳
          </SrText>
        )}
        {role === 'parent' && (
          <SrText font="greycliff" color="grey500" fontSize="12px" weight="700">
            {isExpanded ? '▾' : '▸'}
          </SrText>
        )}
        <SrTooltip
          className="no-icon"
          position={SR_TOOLTIP_POSITION_LEFT}
          tooltipContentStyle={{ maxWidth: '420px' }}
          label={
            <SrText font="greycliff" color="black">
              {token}
            </SrText>
          }
        >
          <SrBox display="flex" flexDirection="column" gap="4px">
            <SrText font="greycliff" color="white">
              <SrText font="greycliff" color="white" weight="700">
                Sender:{' '}
              </SrText>
              {name || '—'}
            </SrText>
            <SrText font="greycliff" color="white">
              <SrText font="greycliff" color="white" weight="700">
                File source:{' '}
              </SrText>
              {sourceSlug || '—'}
            </SrText>
            <SrText font="greycliff" color="white" wordBreak="break-all">
              <SrText font="greycliff" color="white" weight="700">
                Full Gmail sender ID:{' '}
              </SrText>
              {fileToMap.gmailSenderId}
            </SrText>
          </SrBox>
        </SrTooltip>
        {role === 'parent' && (
          <>
            <SrTooltip
              bgColor="orange600"
              position={SR_TOOLTIP_POSITION_CENTER}
              icon={<SrIcon className="warning-icon" cursor="pointer" />}
            >
              This Gmail sender ID appears {count} times
            </SrTooltip>
            <SrText font="greycliff" color="grey500" fontSize="12px">
              {count} files
            </SrText>
          </>
        )}
        {role === 'single' && (
          <SrTooltip
            bgColor="green-dark"
            position={SR_TOOLTIP_POSITION_CENTER}
            icon={<SrIcon className="green-checkmark-small-thick" cursor="pointer" />}
          >
            Safe to map 1:1 — no other file shares this Gmail sender ID
          </SrTooltip>
        )}
      </SrBox>
    );
  };

  const headers = [
    {
      content: 'Downloaded Date',
      dataTableFieldId: 'createdAt',
      width: '20%',
      style: {
        color: 'black',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    {
      content: 'Email Sender',
      width: '25%',
      style: {
        color: 'black',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    {
      content: 'Email Sender Id',
      width: '20%',
      style: {
        color: 'black',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    {
      dataTableFieldId: 'gmailUrl',
      width: '15%',
    },
    {
      width: '20%',
    },
  ];

  const getEmailSenderFromUrl = (url) => {
    const emailSender = url.split('/')[5];
    return emailSender;
  };

  const onPartnerSelected = ({ partner, fileToMap, reloadItems }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_ASSIGN_TO_GMAIL_SENDER,
      payload: {
        utilityPartnerId: partner.id,
        gmailSenderId: fileToMap.gmailSenderId,
        note: `User-mapped from PMS messageId: ${fileToMap.gmailMessageId}`,
      },
      onSuccess: () => {
        setAssignPartnerModalArgs(null);
        return reloadItems({
          count: 1000,
          filter: {
            gmailSenderId: fileToMap.gmailSenderId,
          },
        });
      },
      onError: (error) => {
        setAssignPartnerError(error.message);
      },
    });
  };

  const assignPartnerClick = ({ fileToMap, reloadItems }) => {
    const sender = getEmailSenderFromUrl(fileToMap.url);

    return setAssignPartnerModalArgs({
      subtitle: `Assign "${sender}" file to:`,
      onClose: () => {
        setAssignPartnerModalArgs(null);
        setAssignPartnerError(null);
      },
      onPartnerSelected: (partner) =>
        onPartnerSelected({
          partner,
          fileToMap,
          reloadItems,
        }),
    });
  };

  const handleCopyClick = ({ id, url }) => {
    copyToClipboard(url);
    setCopied(id);
    setTimeout(() => {
      setCopied(null);
    }, 1500);
  };

  return (
    <div>
      {renderLegend()}
      <SrDataTableConnected {...getDataTableSettingsFromProps({ listingId })}>
        {({
          filesToMap,
          reloadItems,
          viewSettings,
          dataTableApiState,
          onInfiniteScrollWrapperRef,
          dataTableIsFullyLoaded,
        }) => {
          // eslint-disable-line max-len
          if (filesToMap.length === 0 && dataTableIsFullyLoaded) {
            return (
              <SrText font="greycliff" weight="500" fontSize="16px">
                All Gmail Attachment files have been mapped to a partner.
              </SrText>
            );
          }
          // Files that share an exact gmailSenderId collapse into one parent row; the rest become
          // children revealed on expand. Count lookup feeds the group hover-highlight.
          const groups = groupFilesBySenderId(filesToMap);
          const groupCountBySenderId = {};
          groups.forEach((group) => {
            groupCountBySenderId[group.senderId] = group.count;
          });

          const buildRow = ({ fileToMap, group, role, isExpanded }) => {
            const senderId = group.senderId;

            // Hover highlight: red across the whole duplicate group, else grey on the single row.
            // The historical theme paints each cell white, so the highlight lives on the cells (not
            // the <tr>) to show through. Red (duplicate group) wins over grey (single-row hover).
            const hoveredIsDuplicate =
              !!hoveredRow && (groupCountBySenderId[hoveredRow.senderId] || 0) > 1;
            const isInHoveredDuplicateGroup =
              hoveredIsDuplicate && hoveredRow.senderId === senderId;
            const isHoveredRow = !!hoveredRow && hoveredRow.rowId === fileToMap.id;
            let cellStyle;
            if (isInHoveredDuplicateGroup) {
              cellStyle = { backgroundColor: DUPLICATE_GROUP_HIGHLIGHT_COLOR };
            } else if (isHoveredRow) {
              cellStyle = { backgroundColor: ROW_HOVER_HIGHLIGHT_COLOR };
            }
            // Indent a child's first cell so it reads as nested under its parent.
            const firstCellStyle =
              role === 'child' ? { ...cellStyle, paddingLeft: CHILD_ROW_INDENT } : cellStyle;

            const row = {
              onMouseEnter: () => setHoveredRow({ rowId: fileToMap.id, senderId }),
              onMouseLeave: () => setHoveredRow(null),
              cells: [
                {
                  content: formatDate(fileToMap.createdAt),
                  style: firstCellStyle,
                },
                {
                  content: getEmailSenderFromUrl(fileToMap.url),
                  style: cellStyle,
                },
                {
                  content: renderSenderIdCell({ fileToMap, count: group.count, role, isExpanded }),
                  style: cellStyle,
                },
                {
                  content: (
                    <SrBox
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCopyClick({ id: fileToMap.id, url: fileToMap.gmailUrl });
                      }}
                    >
                      <SrText font="greycliff" color="blue300" cursor="pointer">
                        {copied === fileToMap.id ? 'Copied!' : 'Copy Email URL'}
                      </SrText>
                    </SrBox>
                  ),
                  style: cellStyle,
                },
                {
                  // Only the parent (or a lone file) is mappable; assigning it maps the whole group.
                  content:
                    role === 'child' ? null : (
                      <SrBox
                        onClick={(event) => {
                          event.stopPropagation();
                          assignPartnerClick({ fileToMap, reloadItems });
                        }}
                      >
                        <SrText font="greycliff" color="blue300" cursor="pointer">
                          Assign to Partner
                        </SrText>
                      </SrBox>
                    ),
                  style: cellStyle,
                },
              ],
            };

            // Clicking a duplicate-group parent toggles its children.
            if (role === 'parent') {
              row.onClick = () => toggleExpanded(senderId);
            }
            return row;
          };

          return (
            <div ref={onInfiniteScrollWrapperRef}>
              <SrDataTableUI
                sticky
                theme={SR_TABLE_THEME_HISTORICAL}
                dataTableViewSettings={viewSettings}
                headers={headers}
                minWidth="100%"
                rows={groups.flatMap((group) => {
                  if (group.count === 1) {
                    return [
                      buildRow({
                        fileToMap: group.parent,
                        group,
                        role: 'single',
                        isExpanded: false,
                      }),
                    ];
                  }
                  const isExpanded = expandedSenderIds.has(group.senderId);
                  const parentRow = buildRow({
                    fileToMap: group.parent,
                    group,
                    role: 'parent',
                    isExpanded,
                  });
                  if (!isExpanded) {
                    return [parentRow];
                  }
                  const childRows = group.children.map((child) =>
                    buildRow({ fileToMap: child, group, role: 'child', isExpanded }),
                  );
                  return [parentRow, ...childRows];
                })}
              />
              {dataTableApiState.inProgress && (
                <div>
                  <div className="loading-ellipses clearfix" />
                </div>
              )}
            </div>
          );
        }}
      </SrDataTableConnected>
      {assignPartnerModalArgs && (
        <UPPartnerSearchForm {...assignPartnerModalArgs} errorMessage={assignPartnerError} />
      )}
    </div>
  );
};

UPAdminPendingGmailAttachmentMappingsDataTable.displayName =
  'UPAdminPendingGmailAttachmentMappingsDataTable';

export default UPAdminPendingGmailAttachmentMappingsDataTable;
